// ReportsPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../scripts/api";
import {
  BarChart3,
  AlertTriangle,
  Timer,
  TrendingUp,
  BadgeCheck,
  Filter,
  RefreshCw,
  Table as TableIcon,
} from "lucide-react";

const TABLE_OPTIONS = [
  { v: "all_inventory", label: "All Inventory" },
  { v: "stock_inventory", label: "Stock Inventory" },
  { v: "used_inventory", label: "Used Inventory" },
  { v: "sold_inventory", label: "Sold Inventory" },
];

// Keep in sync with backend /api/reports/ops presets
const OPS_PRESETS = [
  { v: "alerts", label: "Alerts" },
  { v: "in_stock_calgary", label: "In Stock - Calgary" },
  { v: "arriving_next_14_days", label: "Arriving next 14 days (ETA)" },
  { v: "completing_next_14_days", label: "Completing next 14 days (Ready at Factory)" },
  { v: "in_transit_missing_eta", label: "In transit but missing ETA" },
  { v: "mismatch_status_color", label: "Status/Color mismatch" },
];

const STATUS_BY_CODE = {
  0: { label: 'UNASSIGNED' },
  1: { label: 'SOLD' },
  2: { label: 'RENTED OUT' },
  3: { label: 'IN STOCK - CALGARY' },
  4: { label: 'IN TRANSIT TO CALGARY' },
  5: { label: 'IN TRANSIT TO CUSTOMER' },
  6: { label: 'ALERT' },
  7: { label: 'TO ORDER' },
  8: { label: 'ON ORDER' },
  9: { label: 'STOCK & PDI - IN CALGARY' },
  10: { label: 'DEMO' },
  11: { label: 'CHASSIS ALLOCATION SLOT' },
  12: { label: 'READY AT FACTORY' },
  13: { label: 'IN TRANSIT - BROKE DOWN' },
};

function fmtNum(n) {
  const x = Number(n ?? 0);
  if (!Number.isFinite(x)) return "0";
  return x.toLocaleString();
}

function fmtDays(n) {
  if (n == null) return "-";
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return `${x.toFixed(1)}d`;
}

function fmtMoney(n) {
  const x = Number(n ?? 0);
  if (!Number.isFinite(x)) return "$0";
  return x.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function rowLabel(r) {
  const rowo = r.rowo ? `RO/WO ${r.rowo}` : "";
  const stock = r.stock_po ? `Stock ${r.stock_po}` : "";
  const bits = [rowo, stock].filter(Boolean).join(" • ");
  return bits || `ID #${r.id}`;
}

function SmallPill({ children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        border: "1px solid rgba(255,255,255,0.10)",
        background: "rgba(255,255,255,0.06)",
        lineHeight: 1.8,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function StatTile({ icon: Icon, title, value, sub }) {
  return (
    <div className="scan-tile" style={{ textAlign: "left" }}>
      <div className="scan-tile-top">
        <div className="scan-icon">
          <Icon size={22} />
        </div>
        <div className="scan-chev">•</div>
      </div>
      <div className="scan-title">{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4 }}>{value}</div>
      {sub ? <div className="scan-sub" style={{ marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

function Card({ title, icon: Icon, right, children }) {
  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {Icon ? <Icon size={18} style={{ opacity: 0.9 }} /> : null}
          <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>{title}</div>
        </div>
        {right}
      </div>
      {children}
      
    </div>
  );
}

function SimpleTable({ cols, rows, onRowClick }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.k}
                style={{
                  textAlign: "left",
                  padding: "10px 10px",
                  borderBottom: "1px solid rgba(255,255,255,0.10)",
                  opacity: 0.8,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={r.id ?? idx}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              style={{
                cursor: onRowClick ? "pointer" : "default",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              {cols.map((c) => (
                <td key={c.k} style={{ padding: "10px 10px", verticalAlign: "top" }}>
                  {c.render ? c.render(r) : String(r?.[c.k] ?? "")}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={cols.length} className="muted" style={{ padding: 12 }}>
                No rows.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportsPage() {
  const nav = useNavigate();

  const [table, setTable] = useState("all_inventory");
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const [summary, setSummary] = useState(null);
  const [aging, setAging] = useState(null);
  const [leadtime, setLeadtime] = useState(null);
  const [finance, setFinance] = useState(null);
  const [quality, setQuality] = useState(null);

  const [opsPreset, setOpsPreset] = useState("alerts");
  const [ops, setOps] = useState(null);

  // NEW: completing next 14 days
  const [completing, setCompleting] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const reload = async () => {
    setLoading(true);
    setErrMsg("");
    try {
      const [a, b, c, d, e, f, g] = await Promise.all([
        api.get(`/reports/summary`, { params: { table } }),
        api.get(`/reports/aging`, { params: { table, limit: 200 } }),
        api.get(`/reports/leadtime`, { params: { table } }),
        api.get(`/reports/finance`, { params: { table, groupBy: "unit_category", limit: 800 } }),
        api.get(`/reports/quality`, { params: { table } }),
        api.get(`/reports/ops`, { params: { table, preset: opsPreset, limit: 400 } }),
        api.get(`/reports/ops`, { params: { table, preset: "completing_next_14_days", limit: 400 } }),
      ]);

      setSummary(a.data);
      setAging(b.data);
      setLeadtime(c.data);
      setFinance(d.data);
      setQuality(e.data);
      setOps(f.data);
      setCompleting(g.data);
    } catch (e2) {
      console.error("Reports load failed", e2);
      setErrMsg("Failed to load reports (check console/network).");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, opsPreset]);

  const topColorCounts = useMemo(() => {
    const arr = summary?.byColor || [];
    const nonBlank = arr.filter((x) => String(x.color_code ?? "").trim() !== "");
    return nonBlank
      .slice()
      .sort((a, b) => Number(b.n ?? 0) - Number(a.n ?? 0))
      .slice(0, 6);
  }, [summary]);

  const leadPairs = useMemo(() => {
    const r = leadtime?.results || {};
    const mk = (key, label) => ({
      key,
      label,
      n: r?.[key]?.n ?? 0,
      avg: r?.[key]?.avg_days,
      min: r?.[key]?.min_days,
      max: r?.[key]?.max_days,
    });

    return [
      mk("po_to_factory_order", "PO → Factory Order"),
      mk("factory_order_to_build", "Factory Order → Build"),
      mk("build_to_arrive_sna", "Build → Arrive SNA"),
      mk("arrive_sna_to_customer", "Arrive SNA → To Customer"),
    ];
  }, [leadtime]);

  const financeTop = useMemo(() => {
    const arr = finance?.totals || [];
    return arr.slice(0, 8);
  }, [finance]);

  const openDetail = (r) => {
    if (!r?.id) return;
    nav(`/detail/${table}/${r.id}`);
  };

  return (
    <div className="container space-y">
      <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, letterSpacing: 0.2 }}>Reports</h2>
          <div className="muted" style={{ marginTop: 4 }}>
            Live reporting endpoints (summary • aging • lead time • finance • quality • ops)
          </div>
        </div>

        <div className="row" style={{ gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <Filter size={16} style={{ opacity: 0.85 }} />
            <select className="input" style={{ width: 220 }} value={table} onChange={(e) => setTable(e.target.value)}>
              {TABLE_OPTIONS.map((t) => (
                <option key={t.v} value={t.v}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <AlertTriangle size={16} style={{ opacity: 0.85 }} />
            <select className="input" style={{ width: 260 }} value={opsPreset} onChange={(e) => setOpsPreset(e.target.value)}>
              {OPS_PRESETS.map((p) => (
                <option key={p.v} value={p.v}>
                  Ops: {p.label}
                </option>
              ))}
            </select>
          </div>

          <button className="btn-outline" onClick={reload} disabled={loading} title="Refresh">
            <RefreshCw size={16} style={{ marginRight: 8 }} />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      {errMsg ? <div className="muted">{errMsg}</div> : null}

      <div className="scan-grid" style={{ marginTop: 10 }}>
        <StatTile
          icon={BarChart3}
          title="Total records"
          value={fmtNum(summary?.total ?? 0)}
          sub={summary?.table ? `Table: ${summary.table}` : ""}
        />
        <StatTile
          icon={AlertTriangle}
          title="Alerts"
          value={fmtNum(summary?.flags?.alerts ?? 0)}
          sub="alert = true/1"
        />
        <StatTile
          icon={BadgeCheck}
          title="Stock / Demo / Rental"
          value={`${fmtNum(summary?.flags?.is_stock ?? 0)} / ${fmtNum(summary?.flags?.is_demo ?? 0)} / ${fmtNum(summary?.flags?.is_rental ?? 0)}`}
          sub="flags counts"
        />
        <StatTile
          icon={Timer}
          title="Aging sample"
          value={fmtNum(aging?.count ?? 0)}
          sub="top rows by age_days"
        />
        <StatTile
          icon={TrendingUp}
          title="Quality issues"
          value={fmtNum(
            (quality?.counts?.missingVin ?? 0) +
              (quality?.counts?.missingLocation ?? 0) +
              (quality?.counts?.alertNoNotes ?? 0)
          )}
          sub="missing VIN / location / notes"
        />
        <StatTile
          icon={TableIcon}
          title="Ops preset rows"
          value={fmtNum(ops?.count ?? 0)}
          sub={ops?.title || ""}
        />
      </div>

      <Card
        title="Top ColorCodes"
        icon={BarChart3}
        right={
          <SmallPill>
            <span style={{ opacity: 0.75 }}>Showing</span> {topColorCounts.length}/6
          </SmallPill>
        }
      >
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          {topColorCounts.map((x) => (
            <SmallPill key={String(x.color_code)}>
              <span style={{ fontWeight: 800 }}>#{x.color_code}</span>
              <span style={{ opacity: 0.75 }}>•</span>
              <span>{fmtNum(x.n)}</span>
            </SmallPill>
          ))}
          {topColorCounts.length === 0 ? <div className="muted">No color_code data.</div> : null}
        </div>
      </Card>

      <Card title="Lead Time (avg / min / max)" icon={Timer}>
        <SimpleTable
          cols={[
            { k: "label", label: "Metric" },
            { k: "n", label: "Rows", render: (r) => fmtNum(r.n) },
            { k: "avg", label: "Avg", render: (r) => fmtDays(r.avg) },
            { k: "min", label: "Min", render: (r) => fmtDays(r.min) },
            { k: "max", label: "Max", render: (r) => fmtDays(r.max) },
          ]}
          rows={leadPairs}
        />
        <div className="muted" style={{ marginTop: 10 }}>
          Uses SQLite date columns directly; rows missing either milestone are excluded.
        </div>
      </Card>

      <Card title={`Ops: ${ops?.title || "Preset"}`} icon={AlertTriangle}>
        <SimpleTable
          cols={[
            { k: "id", label: "ID", render: (r) => `#${r.id}` },
            { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
            { k: "customer", label: "Customer" },
            { k: "unit_status", label: "Unit Status" },
            { k: "color_code", label: "Color", render: (r) => (r.color_code ?? "").toString() || "-" },
            { k: "unit_location", label: "Location", render: (r) => r.unit_location || "-" },
          ]}
          rows={ops?.rows || []}
          onRowClick={openDetail}
        />
        <div className="muted" style={{ marginTop: 10 }}>
          Click a row to open details and fix it.
        </div>
      </Card>

      {/* NEW: completing in next 14 days */}
      <Card
        title="Completing next 14 days"
        icon={Timer}
        right={<SmallPill>{fmtNum(completing?.count ?? 0)} rows</SmallPill>}
      >
        <SimpleTable
          cols={[
            { k: "id", label: "ID", render: (r) => `#${r.id}` },
            { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
            { k: "customer", label: "Customer" },
            { k: "ready_at_factory", label: "Ready at Factory", render: (r) => r.ready_at_factory || "-" },
            { k: "unit_status", label: "Unit Status" },
            { k: "color_code", label: "Color", render: (r) => (r.color_code ?? "").toString() || "-" },
            { k: "unit_location", label: "Location", render: (r) => r.unit_location || "-" },
          ]}
          rows={completing?.rows || []}
          onRowClick={openDetail}
        />
        <div className="muted" style={{ marginTop: 10 }}>
          Based on <b>ready_at_factory</b> within the next 14 days.
        </div>
      </Card>

      <Card title="Aging (age_days desc)" icon={Timer}>
        <SimpleTable
          cols={[
            { k: "id", label: "ID", render: (r) => `#${r.id}` },
            { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
            { k: "customer", label: "Customer" },
            { k: "unit_status", label: "Unit Status" },
            { k: "color_code", label: "Color", render: (r) => (r.color_code ?? "").toString() || "-" },
            { k: "age_date", label: "Age date", render: (r) => r.age_date || "-" },
            { k: "age_days", label: "Age days", render: (r) => (r.age_days == null ? "-" : String(r.age_days)) },
          ]}
          rows={aging?.rows || []}
          onRowClick={openDetail}
        />
      </Card>

      <Card title="Finance (by unit_category)" icon={TrendingUp}>
        <SimpleTable
          cols={[
            { k: "key", label: "Category" },
            { k: "count", label: "Units", render: (r) => fmtNum(r.count) },
            { k: "total_cost", label: "Total Cost", render: (r) => fmtMoney(r.total_cost) },
            { k: "total_sell", label: "Total Sell", render: (r) => fmtMoney(r.total_sell) },
            { k: "total_margin", label: "Margin", render: (r) => fmtMoney(r.total_margin) },
          ]}
          rows={financeTop}
        />
        <div className="muted" style={{ marginTop: 10 }}>
          This uses computed totals from the backend. If you already store full totals, adjust the backend math to avoid double counting.
        </div>
      </Card>

      <Card title="Data Quality" icon={BadgeCheck}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <SmallPill>Missing VIN: <b style={{ marginLeft: 6 }}>{fmtNum(quality?.counts?.missingVin ?? 0)}</b></SmallPill>
          <SmallPill>In-stock missing Location: <b style={{ marginLeft: 6 }}>{fmtNum(quality?.counts?.missingLocation ?? 0)}</b></SmallPill>
          <SmallPill>Alert missing Notes: <b style={{ marginLeft: 6 }}>{fmtNum(quality?.counts?.alertNoNotes ?? 0)}</b></SmallPill>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, alignItems: "start" }}>
          <div className="card" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Missing VIN (sample)</div>
            <SimpleTable
              cols={[
                { k: "id", label: "ID", render: (r) => `#${r.id}` },
                { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
                { k: "customer", label: "Customer" },
                { k: "unit_status", label: "Status" },
              ]}
              rows={quality?.samples?.missingVin || []}
              onRowClick={openDetail}
            />
          </div>

          <div className="card" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>In-stock missing Location (sample)</div>
            <SimpleTable
              cols={[
                { k: "id", label: "ID", render: (r) => `#${r.id}` },
                { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
                { k: "customer", label: "Customer" },
                { k: "color_code", label: "Color" },
              ]}
              rows={quality?.samples?.missingLocation || []}
              onRowClick={openDetail}
            />
          </div>

          <div className="card" style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Alert missing Notes (sample)</div>
            <SimpleTable
              cols={[
                { k: "id", label: "ID", render: (r) => `#${r.id}` },
                { k: "label", label: "RO/WO + Stock", render: (r) => rowLabel(r) },
                { k: "customer", label: "Customer" },
                { k: "unit_status", label: "Status" },
              ]}
              rows={quality?.samples?.alertNoNotes || []}
              onRowClick={openDetail}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}